import React, { useState, useEffect } from 'react';
import { Space, Select, Tag, Tooltip, Modal, Input, Button, message, Table, DatePicker } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { i18nService } from '../../i18n/i18nService';
import { useI18n } from '../../i18n/useI18n';
import CalibrationModal from './CalibrationModal';
import SystemSyncModal from './SystemSyncModal';
import { AmplifierProjectData, AmplifierControlData, AmplifierData, ProgramStatusMessage } from './amplifier';
import { amplifierMqttService } from './AmplifierMqttService';
import type { ProgramStatusResponseMessage, HamlibControlRequestMessage, HamlibControlResponseMessage, UpdateAlarmLogRequestMessage } from './amplifier';

type TempAmplifierControlData = AmplifierControlData;

interface TopBarHeaderProps {
    isConnected: boolean;
    isMcuConnected: boolean;
    onAntennaChange: (mode: 'manual' | 'auto') => void;
    onTriggerModeChange: (mode: 'PTT1' | 'PP2' | '输入功率触发') => void;
    onLanguageChange: (lang: 'zh' | 'en' | 'jp') => void;
    triggerMode?: number;
    currentTriggerMode?: 'PTT1' | 'PP2' | '输入功率触发';
    amplifierProjectData?: AmplifierProjectData;
    amplifierControlData?: AmplifierControlData;
    onAmplifierControlDataChange?: (controlData: AmplifierControlData) => void;
    deviceId: string;
    amplifierData?: AmplifierData | null;
    programStatusMessage?: ProgramStatusMessage | null;
    hamlibControlRequestMessage?: HamlibControlRequestMessage | null;
}

export const TopBarHeader: React.FC<TopBarHeaderProps> = ({
    isConnected,
    isMcuConnected,
    onAntennaChange,
    onTriggerModeChange,
    onLanguageChange,
    triggerMode,
    currentTriggerMode,
    amplifierProjectData,
    amplifierControlData,
    onAmplifierControlDataChange,
    deviceId,
    amplifierData,
    programStatusMessage,
    hamlibControlRequestMessage
}) => {
    const { t } = useI18n();
    const [language, setLanguage] = useState<'zh' | 'en' | 'jp'>('zh');

    // 添加临时 AmplifierControlData 状态，用于前端临时存储修改的值
    const [tempAmplifierControlData, setTempAmplifierControlData] = useState<TempAmplifierControlData>({
        frequency: 0,
        triggerMode: 0,
        tuneMode: 0,
        power: 0,
        inputAttenuator: 0,
        inputAntenna: 0,
        outputAntenna: 0,
        alarmWord: 0
    });

    // 天线模式状态
    const [antennaMode, setAntennaMode] = useState<'manual' | 'auto'>('manual');

    // 触发模式状态：0=PTT1(默认) 1=PP2 3=输入功率触发（已删除接收模式）
    const [currentTriggerModeState, setCurrentTriggerModeState] = useState<'PTT1' | 'PP2' | '输入功率触发'>('PTT1');
    const [systemTime, setSystemTime] = useState<string>(() => {
        const now = new Date();
        return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
    });
    const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
    const [isCalibrationModalVisible, setIsCalibrationModalVisible] = useState(false);
    const [isSystemSyncModalVisible, setIsSystemSyncModalVisible] = useState(false);
    const [isAlarmInfoModalVisible, setIsAlarmInfoModalVisible] = useState(false);
    const [password, setPassword] = useState('');
    const [alarmLogStartTime, setAlarmLogStartTime] = useState<Dayjs | null>(() => dayjs());
    const [alarmLogEndTime, setAlarmLogEndTime] = useState<Dayjs | null>(() => dayjs());
    const [alarmLogPageSize, setAlarmLogPageSize] = useState<number>(10);
    const [alarmLogMsgId, setAlarmLogMsgId] = useState<number>(1);
    /** 报警日志列表（由后端/MQTT 回包通过 setAlarmLogDataSource 填充），是否分页由实际条数决定，与前端 count 选择无关 */
    const [alarmLogDataSource, setAlarmLogDataSource] = useState<Array<{ key: number | string; time: string; level: string; message: string }>>([]);
    const [alarmLogCurrentPage, setAlarmLogCurrentPage] = useState<number>(1);

    const alarmLogTotalPages = alarmLogDataSource.length > 10 ? Math.ceil(alarmLogDataSource.length / 10) : 1;
    useEffect(() => {
        setAlarmLogCurrentPage((prev) => {
            const totalPages = alarmLogDataSource.length > 10 ? Math.ceil(alarmLogDataSource.length / 10) : 1;
            return Math.min(prev, totalPages) || 1;
        });
    }, [alarmLogDataSource.length]);

    useEffect(() => {
        const timer = setInterval(() => {
            const now = new Date();
            const timeString = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
            setSystemTime(timeString);
        }, 1000);

        return () => clearInterval(timer);
    }, []);



    // 当 amplifierControlData 变化时，更新临时控制数据
    useEffect(() => {
        if (amplifierControlData) {
            setTempAmplifierControlData(amplifierControlData);
        }
    }, [amplifierControlData]);

    // 根据 triggerMode 数字同步触发模式显示：0=PTT1 1=PP2 3=输入功率触发（2 兼容显示为 PP2）
    useEffect(() => {
        if (triggerMode !== undefined) {
            const numberToLabel: { [key: number]: 'PTT1' | 'PP2' | '输入功率触发' } = {
                0: 'PTT1',
                1: 'PP2',
                2: 'PP2',
                3: '输入功率触发'
            };
            setCurrentTriggerModeState(numberToLabel[triggerMode] ?? 'PTT1');
        }
    }, [triggerMode]);

    const handleLanguageChange = (value: 'zh' | 'en' | 'jp') => {
        setLanguage(value);
        i18nService.setLanguage(value);
        onLanguageChange(value);
    };

    const handleAntennaChange = async () => {
        // 切换天线模式
        const newMode = antennaMode === 'manual' ? 'auto' : 'manual';
        setAntennaMode(newMode);

        // 调用父组件的回调函数
        onAntennaChange(newMode);

        // 发送指令
        try {
            console.log('TopBarHeader: 切换天线模式:', newMode);
        } catch (error) {
            console.error('TopBarHeader: 天线模式切换失败:', error);
        }
    };

    const handleTriggerModeChange = async () => {
        // 循环切换触发模式：0=PTT1(默认) 1=PP2 3=输入功率触发
        const triggerModes: ('PTT1' | 'PP2' | '输入功率触发')[] = ['PTT1', 'PP2', '输入功率触发'];
        const currentIndex = triggerModes.indexOf(currentTriggerModeState);
        const nextIndex = (currentIndex + 1) % triggerModes.length;
        const newMode = triggerModes[nextIndex];

        onTriggerModeChange(newMode);

        const triggerModeMap: { [key: string]: number } = {
            'PTT1': 0,
            'PP2': 1,
            '输入功率触发': 3
        };

        const modeValue = triggerModeMap[newMode] ?? 0;
        // PTT1↔输入天线1、PP2↔输入天线2 双向绑定，切换触发模式时一并下发对应 inputAntenna
        const updatedControlData: TempAmplifierControlData = {
            ...tempAmplifierControlData,
            triggerMode: modeValue,
            ...(modeValue === 0 && { inputAntenna: 1 }),
            ...(modeValue === 1 && { inputAntenna: 2 })
        };

        try {
            await amplifierMqttService.sendAmpConfigCommand(deviceId, updatedControlData);
            console.log('TopBarHeader: 触发模式更新指令发送成功:', updatedControlData);
        } catch (error) {
            console.error('TopBarHeader: 触发模式更新指令发送失败:', error);
        }
        // 不在此处 setCurrentTriggerModeState / setTempAmplifierControlData，等 MQTT 回包后由 triggerMode/amplifierData 驱动渲染，避免与设备不同步
    };

    const getConnectionStatusLabel = (connected: boolean) => {
        return connected ? t('Connected') : t('Disconnected');
    };

    const getConnectionStatusColor = (connected: boolean) => {
        return connected ? 'green' : 'red';
    };

    const handleCalibrationButtonClick = () => {
        setIsPasswordModalVisible(true);
        setPassword('');
    };

    const handlePasswordConfirm = () => {
        if (password === '666') {
            setIsPasswordModalVisible(false);
            setIsCalibrationModalVisible(true);
            message.success('密码验证成功');
        } else {
            message.error('密码错误，请重试');
            setPassword('');
        }
    };

    const handlePasswordCancel = () => {
        setIsPasswordModalVisible(false);
        setPassword('');
    };

    const handleCalibrationConfirm = () => {
        setIsCalibrationModalVisible(false);
        message.success('工程参数设置已保存');
    };

    const handleCalibrationCancel = () => {
        setIsCalibrationModalVisible(false);
    };

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 'var(--spacing-sm) var(--spacing-lg)',
            backgroundColor: 'var(--color-bg-card)',
            borderBottom: '1px solid var(--color-border-primary)',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                <Select
                    size="small"
                    value={language}
                    onChange={handleLanguageChange}
                    options={[
                        { value: 'zh', label: 'CH' },
                        { value: 'en', label: 'EN' },
                        { value: 'jp', label: 'JP' }
                    ]}
                    style={{ width: 80 }}
                    bordered={false}
                />
                <Tag color="green">{t('Hamlib: Connected')}</Tag>
                <Tag color="red">{t('MUC: 未连接')}</Tag>
            </div>

            <Space size="small">
                {/* <Button
                    size="small"
                    type="primary"
                    onClick={handleCalibrationButtonClick}
                    style={{
                        backgroundColor: '#1890ff',
                        borderColor: '#1890ff'
                    }}
                >
                    工程参数设置
                </Button> */}

                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                    <span style={{ fontSize: 13 }}>{t('输出天线：')}</span>
                    <Button
                        size="small"
                        type={antennaMode === 'auto' ? 'primary' : 'default'}
                        onClick={handleAntennaChange}
                        style={{
                            width: 100,
                            backgroundColor: antennaMode === 'auto' ? 'green' : '#1890ff',
                            borderColor: antennaMode === 'auto' ? 'green' : '#1890ff',
                            color: 'white'
                        }}
                    >
                        {antennaMode === 'manual' ? t('手动选择') : t('自动选择')}
                    </Button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                    <span style={{ fontSize: 13 }}>{t('触发模式：')}</span>
                    <Button
                        size="small"
                        type="primary"
                        onClick={handleTriggerModeChange}
                        style={{
                            width: 120,
                            backgroundColor: 'green',
                            borderColor: 'green',
                            color: 'white'
                        }}
                    >
                        {currentTriggerModeState}
                    </Button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                    <span style={{ fontSize: 13 }}>{systemTime}</span>
                    <Button
                        size="small"
                        type="primary"
                        onClick={() => setIsSystemSyncModalVisible(true)}
                        style={{
                            backgroundColor: 'green',
                            borderColor: 'green',
                            color: 'white'
                        }}
                    >
                        {t('系统同步')}
                    </Button>
                    <span style={{ fontSize: 13 }}>{t('时间')}</span>
                    <Button
                        size="small"
                        type="primary"
                        danger
                        onClick={() => setIsAlarmInfoModalVisible(true)}
                        style={{
                            backgroundColor: '#ff4d4f',
                            borderColor: '#ff4d4f',
                            color: 'white'
                        }}
                    >
                        {t('报警信息')}
                    </Button>
                </div>
            </Space>

            <Modal
                title="密码验证"
                open={isPasswordModalVisible}
                onOk={handlePasswordConfirm}
                onCancel={handlePasswordCancel}
                okText="确认"
                cancelText="取消"
            >
                <div style={{ marginBottom: '16px' }}>
                    <Input
                        type="password"
                        placeholder="请输入密码"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onPressEnter={handlePasswordConfirm}
                        maxLength={10}
                        style={{ width: '100%' }}
                    />
                </div>
                <div style={{ color: '#666', fontSize: '12px' }}>
                    提示：密码为 666
                </div>
            </Modal>

            <CalibrationModal
                isOpen={isCalibrationModalVisible}
                onClose={handleCalibrationCancel}
                onConfirm={handleCalibrationConfirm}
                amplifierProjectData={amplifierProjectData}
                deviceId={deviceId}
            />

            <SystemSyncModal
                isOpen={isSystemSyncModalVisible}
                onClose={() => setIsSystemSyncModalVisible(false)}
                programStatusMessage={programStatusMessage}
                onProgramStatusSave={(msg: ProgramStatusResponseMessage) => amplifierMqttService.sendJsonCommand(deviceId, msg)}
                hamlibControlRequestMessage={hamlibControlRequestMessage}
                onHamlibSave={(msg: HamlibControlResponseMessage) => amplifierMqttService.sendJsonCommand(deviceId, msg)}
            />

            {/* 报警信息弹窗 - 深色主题 */}
            <Modal
                open={isAlarmInfoModalVisible}
                onCancel={() => setIsAlarmInfoModalVisible(false)}
                footer={null}
                width={960}
                rootClassName="alarm-info-modal"
                styles={{
                    body: { padding: 0, backgroundColor: '#282c34' },
                    content: { backgroundColor: '#282c34' },
                    header: { display: 'none' }
                }}
            >
                <div style={{
                    display: 'flex',
                    minHeight: 400,
                    backgroundColor: '#282c34',
                    color: '#e0e0e0'
                }}>
                    {/* 左侧：启用报警信息 */}
                    <div style={{
                        flex: '0 0 280px',
                        padding: 16,
                        borderRight: '1px solid #3e4451',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, color: '#e0e0e0' }}>
                            启用报警信息
                        </div>
                        <ul style={{
                            listStyle: 'none',
                            padding: 0,
                            margin: 0,
                            flex: 1,
                            overflow: 'auto',
                            fontSize: 13,
                            color: '#b0b0b0'
                        }}>
                            {[
                                '输入正向功率保护',
                                '输入反向功率保护',
                                '功放管输入过电压保护',
                                '功放管输出过电压保护',
                                '功放输出正向功率保护',
                                '功放管输出反向功率保护',
                                '过流保护',
                                '过压保护',
                                '过温保护',
                                '高驻波',
                                '频率误差过大'
                            ].map((label, i) => {
                                const alarmWord = amplifierData?.alarmWord ?? 0;
                                const isAlarm = (alarmWord & (1 << i)) !== 0;
                                return (
                                    <li key={i} style={{
                                        padding: '6px 0',
                                        color: isAlarm ? '#ff4d4f' : undefined
                                    }}>
                                        {label}
                                    </li>
                                );
                            })}
                        </ul>
                        <div style={{ textAlign: 'center', marginTop: 12 }}>
                            <Button
                                type="default"
                                onClick={() => {
                                    if (!programStatusMessage?.data) {
                                        message.warning('暂无程序状态，无法发送清除报警');
                                        return;
                                    }
                                    const responseMsg: ProgramStatusResponseMessage = {
                                        ...programStatusMessage,
                                        type: 'response',
                                        cmd: 'update_program_status',
                                        timestamp: Date.now(),
                                        data: { ...programStatusMessage.data, command: 2 }
                                    };
                                    amplifierMqttService.sendJsonCommand(deviceId, responseMsg).then(() => {
                                        message.success('清除报警指令已发送');
                                    }).catch((err) => {
                                        console.error('清除报警发送失败:', err);
                                        message.error('清除报警发送失败');
                                    });
                                }}
                                style={{
                                    backgroundColor: '#3e4451',
                                    borderColor: '#5a6270',
                                    color: '#e0e0e0'
                                }}
                            >
                                清除报警
                            </Button>
                        </div>
                    </div>
                    {/* 右侧：报警日志 */}
                    <div style={{ flex: 1, padding: 16, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <div style={{ textAlign: 'center', fontSize: 15, fontWeight: 600, marginBottom: 4, color: '#e0e0e0' }}>
                            报警日志
                        </div>
                        <div style={{ fontSize: 13, marginBottom: 8, color: '#b0b0b0' }}>
                            日志事件
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                            <span style={{ color: '#b0b0b0', fontSize: 12 }}>开始时间：</span>
                            <DatePicker
                                format="YYYY-MM-DD"
                                value={alarmLogStartTime}
                                onChange={(v) => setAlarmLogStartTime(v)}
                                placeholder="选择开始日期"
                                style={{ width: 130 }}
                                allowClear
                            />
                            <span style={{ color: '#b0b0b0', fontSize: 12 }}>结束时间：</span>
                            <DatePicker
                                format="YYYY-MM-DD"
                                value={alarmLogEndTime}
                                onChange={(v) => setAlarmLogEndTime(v)}
                                placeholder="选择结束日期"
                                style={{ width: 130 }}
                                allowClear
                            />
                            <span style={{ color: '#b0b0b0', fontSize: 12, marginLeft: 8 }}>数量：</span>
                            <Select
                                value={alarmLogPageSize}
                                onChange={setAlarmLogPageSize}
                                options={[
                                    { value: 5, label: '5' },
                                    { value: 10, label: '10' },
                                    { value: 20, label: '20' },
                                    { value: 50, label: '50' },
                                    { value: 100, label: '100' }
                                ]}
                                style={{ width: 72 }}
                            />
                        </div>
                        <div style={{ flex: 1, overflow: 'auto' }}>
                            <Table
                                size="small"
                                dataSource={alarmLogDataSource}
                                columns={[
                                    { title: '时间', dataIndex: 'time', key: 'time', width: 170, render: (v: string) => <span style={{ color: '#e0e0e0' }}>{v}</span> },
                                    { title: '等级', dataIndex: 'level', key: 'level', width: 80, render: (v: string) => <span style={{ color: '#ff4d4f' }}>{v}</span> },
                                    { title: '消息', dataIndex: 'message', key: 'message', ellipsis: true, render: (v: string) => <span style={{ color: '#e0e0e0' }}>{v}</span> }
                                ]}
                                pagination={alarmLogDataSource.length > 10 ? {
                                    total: alarmLogDataSource.length,
                                    pageSize: 10,
                                    current: Math.min(alarmLogCurrentPage, alarmLogTotalPages) || 1,
                                    onChange: (page: number) => setAlarmLogCurrentPage(page),
                                    showSizeChanger: false,
                                    showTotal: (t: number) => `共 ${t} 条`,
                                    showQuickJumper: true,
                                    showPrevNextJumpers: true
                                } : false}
                                style={{ backgroundColor: 'transparent' }}
                                className="alarm-log-table"
                            />
                        </div>
                    </div>
                </div>
                {/* 底部操作按钮 */}
                <div style={{
                    padding: '12px 16px',
                    borderTop: '1px solid #3e4451',
                    backgroundColor: '#282c34',
                    textAlign: 'right',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 8
                }}>
                    <Button
                        type="default"
                        onClick={() => {
                            const start = alarmLogStartTime ? dayjs(alarmLogStartTime).format('YYYY-MM-DD 00:00:00') : '';
                            const end = alarmLogEndTime ? dayjs(alarmLogEndTime).format('YYYY-MM-DD 23:59:59') : '';
                            const msg: UpdateAlarmLogRequestMessage = {
                                msg_id: String(alarmLogMsgId),
                                type: 'request',
                                cmd: 'update_alarm_log',
                                timestamp: Math.floor(Date.now() / 1000),
                                data: { count: alarmLogPageSize, start_time: start, end_time: end }
                            };
                            amplifierMqttService.sendJsonCommand(deviceId, msg).then(() => {
                                setAlarmLogMsgId((id) => id + 1);
                                message.success('更新报警日志指令已发送');
                            }).catch((err) => {
                                console.error('更新报警日志发送失败:', err);
                                message.error('更新报警日志发送失败');
                            });
                        }}
                        style={{ backgroundColor: '#3e4451', borderColor: '#5a6270', color: '#e0e0e0' }}
                    >
                        更新
                    </Button>
                    <Button
                        type="default"
                        onClick={() => {
                            if (!programStatusMessage?.data) {
                                message.warning('暂无程序状态，无法发送导出');
                                return;
                            }
                            const responseMsg: ProgramStatusResponseMessage = {
                                ...programStatusMessage,
                                type: 'response',
                                cmd: 'update_program_status',
                                timestamp: Date.now(),
                                data: { ...programStatusMessage.data, command: 3 }
                            };
                            amplifierMqttService.sendJsonCommand(deviceId, responseMsg).then(() => {
                                message.success('导出指令已发送');
                            }).catch((err) => {
                                console.error('导出发送失败:', err);
                                message.error('导出发送失败');
                            });
                        }}
                        style={{ backgroundColor: '#3e4451', borderColor: '#5a6270', color: '#e0e0e0' }}
                    >
                        导出
                    </Button>
                    <Button
                        type="default"
                        onClick={() => setIsAlarmInfoModalVisible(false)}
                        style={{ backgroundColor: '#3e4451', borderColor: '#5a6270', color: '#e0e0e0' }}
                    >
                        取消
                    </Button>
                    <Button
                        type="primary"
                        onClick={() => setIsAlarmInfoModalVisible(false)}
                        style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                    >
                        确认
                    </Button>
                </div>
            </Modal>
        </div>
    );
};

export default TopBarHeader;